/* Stor Atelier theme behaviour that should survive vendor theme updates. */

// Hand off between overlays sequentially so their focus traps and scroll locks
// do not overlap. The real search URL remains the no-JavaScript fallback.
(() => {
  let openingSearch = false;
  document.addEventListener('click', async event => {
    const link = event.target.closest?.('#menu-drawer [aria-controls="search-drawer"]');
    if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const menu = link.closest('header-menu-drawer');
    const search = document.getElementById('search-drawer');
    if (typeof menu?.hide !== 'function' || typeof search?.show !== 'function') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (openingSearch) return;
    openingSearch = true;
    try {
      await menu.hide();
      // Give the search focus trap a visible return target, not the hidden link.
      menu.controls.find(control => control.getClientRects().length)?.focus({ preventScroll: true });
      await search.show();
      search.querySelector('input[name="q"]')?.focus();
    } finally {
      openingSearch = false;
    }
  }, true);
})();

// Same-page drawer links must release the focus trap and scroll lock before
// moving to the destination (including Newsletter in the shared footer).
document.addEventListener('click', async event => {
  const link = event.target.closest?.('#menu-drawer a[href]');
  if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  const url = new URL(link.href, location.href);
  if (!url.hash || url.origin !== location.origin || url.pathname !== location.pathname || url.search !== location.search) return;
  const target = document.getElementById(decodeURIComponent(url.hash.slice(1)));
  const menu = link.closest('header-menu-drawer');
  if (!target || typeof menu?.hide !== 'function') return;
  event.preventDefault();
  // The anchor destination takes focus, not the menu's original activator.
  menu.focusTrap?.deactivate({ returnFocus: false });
  await menu.hide();
  target.scrollIntoView({ behavior: 'instant', block: 'center' });
  target.querySelector('input:not([type="hidden"]), a[href], button')?.focus({ preventScroll: true });
});

// Priced gift choices are real Shopify variants on the hidden Gift Options
// product. Adding both variants together makes the surcharge native to bag,
// checkout, orders and refunds; private properties keep the two lines paired.
(() => {
  const propertyName = 'Select Gift Options';
  const choicesByUrl = new Map();
  let updating = false;
  const root = () => window.Shopify?.routes?.root || '/';
  const newGroup = () => globalThis.crypto?.randomUUID?.() || `gift-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  const triggerFor = form => form.closest('line-item')?.querySelector('[data-gift-editor-toggle]');
  const statusFor = form => form.querySelector('.line-item__gift-editor-status');
  const messageFrom = (json, fallback) => typeof json?.description === 'string' ? json.description : json?.message || fallback;

  function selectedChoice(select) {
    const option = select.selectedOptions[0];
    return { id: select.value, label: option?.dataset.giftLabel || option?.textContent?.split(' — ')[0].trim() || '' };
  }

  function syncGiftNote(select) {
    const field = select.closest('[data-gift-editor], .v-stack')?.querySelector('[data-gift-note-field]');
    if (!field) return;
    const enabled = Boolean(select.value) && /handwritten|gift card/i.test(selectedChoice(select).label);
    field.hidden = !enabled;
    field.querySelector('textarea').disabled = !enabled;
  }

  const syncGiftNotes = () => document.querySelectorAll('select[name="gift_option_variant"], select[name="gift_option"]').forEach(syncGiftNote);
  document.addEventListener('change', event => {
    if (event.target.matches?.('select[name="gift_option_variant"], select[name="gift_option"]')) syncGiftNote(event.target);
  });
  new MutationObserver(syncGiftNotes).observe(document.documentElement, { childList: true, subtree: true });
  syncGiftNotes();

  document.addEventListener('cart:change', event => {
    // Run after the vendor's count listener, which includes hidden gift lines.
    if (event.detail?.cart) queueMicrotask(() => syncVisibleCartCount(event.detail.cart));
  });

  function syncVisibleCartCount(cart) {
    const count = cart.items.reduce((total, item) => item.handle === 'gift-options' ? total : total + item.quantity, 0);
    document.querySelectorAll('cart-count').forEach(element => { element.itemCount = count; });
  }

  function propertiesFrom(formData) {
    const properties = {};
    for (const [name, value] of formData.entries()) {
      const match = name.match(/^properties\[(.*)]$/);
      if (match && value !== '') properties[match[1]] = value;
    }
    return properties;
  }

  async function request(path, body) {
    const response = await fetch(`${root()}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body),
    });
    const json = await response.json();
    if (!response.ok) throw new Error(messageFrom(json, 'Unable to update gift options. Please try again.'));
    return json;
  }

  // Capture before Stretch's product-form listener so a priced gift selection
  // becomes one multi-item Ajax add rather than an unpriced text property.
  document.addEventListener('submit', async event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.action.includes('/cart/add')) return;
    const select = form.elements.namedItem('gift_option_variant');
    if (!(select instanceof HTMLSelectElement) || !select.value || form.dataset.pricedGiftSubmitting === 'true') return;

    event.preventDefault();
    event.stopImmediatePropagation();
    if (!form.checkValidity()) { form.reportValidity(); return; }

    const choice = selectedChoice(select);
    const formData = new FormData(form);
    const productVariant = formData.get('id');
    const quantity = Math.max(1, Number(formData.get('quantity') || 1));
    if (!productVariant) return;

    const group = newGroup();
    const properties = propertiesFrom(formData);
    properties[propertyName] = choice.label;
    properties._gift_group = group;
    properties._gift_variant = choice.id;

    const submitButtons = [...form.elements].filter(element => element instanceof HTMLButtonElement && element.type === 'submit');
    submitButtons.forEach(button => button.setAttribute('aria-busy', 'true'));
    form.dataset.pricedGiftSubmitting = 'true';
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

    try {
      const sections = new Set();
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
      const responseJson = await request('cart/add.js', {
        items: [
          { id: String(productVariant), quantity, properties },
          { id: String(choice.id), quantity: 1, properties: { _gift_group: group, _gift_addon: 'true', _gift_for: formData.get('product-title') || '' } },
        ],
        sections: [...sections].slice(0, 5),
        sections_url: location.pathname,
      });

      if (window.themeVariables.settings.cartType === 'page' || window.themeVariables.settings.pageType === 'cart') {
        window.location.href = `${root()}cart`;
        return;
      }

      const cart = await (await fetch(`${root()}cart.js`, { cache: 'no-store' })).json();
      cart.sections = responseJson.sections;
      const items = responseJson.items || [];
      form.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: { items, cart } }));
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true, detail: { baseEvent: 'variant:add', cart },
      }));
      syncVisibleCartCount(cart);
    } catch (error) {
      form.dispatchEvent(new CustomEvent('cart:error', { bubbles: true, detail: { error: error.message } }));
      document.dispatchEvent(new CustomEvent('toast:show', { detail: { message: error.message, tone: 'error' } }));
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    } finally {
      delete form.dataset.pricedGiftSubmitting;
      submitButtons.forEach(button => button.removeAttribute('aria-busy'));
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
    }
  }, true);

  document.addEventListener('cart-drawer:refreshed', async () => {
    const response = await fetch(`${root()}cart.js`, { cache: 'no-store' });
    if (response.ok) syncVisibleCartCount(await response.json());
  });

  // Removing merchandise must also remove its hidden priced gift line. The
  // same handler covers the drawer link and the separate remove link in cart.
  document.addEventListener('change', event => {
    const input = event.target;
    if (!input.matches?.('.quantity-selector__input[data-line-key]') || Number(input.value) !== 0) return;
    const item = input.closest('line-item') || input.closest('tr')?.querySelector('line-item');
    if (!item?.dataset.pricedGiftAddonKey) return;
    const remove = item.querySelector('.line-item__remove-button[href]');
    if (!remove) return;
    event.stopImmediatePropagation();
    remove.click();
  }, true);

  document.addEventListener('click', async event => {
    const link = event.target.closest('a[href]');
    if (!link) return;
    const lineItem = link.closest('line-item') || link.closest('tr')?.querySelector('line-item');
    const parentKey = lineItem?.dataset.pricedGiftParentKey;
    const giftKey = lineItem?.dataset.pricedGiftAddonKey;
    if (!parentKey || !giftKey) return;

    const url = new URL(link.href, location.origin);
    if (!url.pathname.endsWith('/cart/change') || url.searchParams.get('quantity') !== '0') return;

    event.preventDefault();
    event.stopImmediatePropagation();
    link.setAttribute('aria-busy', 'true');

    try {
      await request('cart/update.js', { updates: { [parentKey]: 0, [giftKey]: 0 } });
      if (window.themeVariables.settings.pageType === 'cart') {
        location.reload();
      } else {
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      }
    } catch (error) {
      link.removeAttribute('aria-busy');
      document.dispatchEvent(new CustomEvent('toast:show', { detail: { message: error.message, tone: 'error' } }));
    }
  }, true);

  async function loadChoices(form) {
    const url = new URL(form.dataset.productUrl, location.origin);
    if (url.origin !== location.origin) throw new Error('Invalid product link');
    if (!choicesByUrl.has(url.href)) {
      const pending = fetch(url.href).then(async response => {
        if (!response.ok) throw new Error('Unable to load gift options. Please try again.');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const source = html.querySelector('select[name="gift_option_variant"]');
        if (!source) throw new Error('Gift options are not available for this item.');
        return [...source.options].filter(option => !option.disabled).map(option => ({
          value: option.value,
          label: option.textContent.trim(),
          giftLabel: option.dataset.giftLabel || option.textContent.split(' — ')[0].trim(),
        }));
      }).catch(error => { choicesByUrl.delete(url.href); throw error; });
      choicesByUrl.set(url.href, pending);
    }

    const choices = await choicesByUrl.get(url.href);
    if (!choices.length) throw new Error('Gift options are not available for this item.');
    const select = form.elements.gift_option;
    select.replaceChildren(...choices.map(choice => {
      const option = new Option(choice.label, choice.value);
      option.dataset.giftLabel = choice.giftLabel;
      return option;
    }));
    const currentVariant = form.dataset.currentGiftVariant;
    const currentLabel = form.dataset.currentGift;
    const current = choices.find(choice => choice.value === currentVariant)
      || choices.find(choice => choice.giftLabel === currentLabel)
      || choices[0];
    select.value = current.value;
    form.dataset.initialGift = current.value;
    select.disabled = false;
    form.querySelector('[type="submit"]').disabled = false;
    form.dataset.loaded = 'true';
    syncGiftNote(select);
  }

  document.addEventListener('click', async event => {
    const target = event.target instanceof Element ? event.target : null;
    const toggle = target?.closest('[data-gift-editor-toggle]');
    const cancel = target?.closest('[data-gift-editor-cancel]');
    if (!toggle && !cancel) return;
    const form = toggle ? document.getElementById(toggle.getAttribute('aria-controls')) : cancel.closest('[data-gift-editor]');
    if (!form || form.getAttribute('aria-busy') === 'true') return;
    const opening = toggle && form.hidden;
    form.hidden = !opening;
    triggerFor(form)?.setAttribute('aria-expanded', String(Boolean(opening)));
    if (!opening) { triggerFor(form)?.focus(); return; }
    statusFor(form).textContent = '';
    form.querySelector('.line-item__gift-editor-fallback').hidden = true;
    try {
      if (!form.dataset.loaded) await loadChoices(form);
      else form.elements.gift_option.value = form.dataset.initialGift;
      if (form.elements.gift_note) form.elements.gift_note.value = form.elements.gift_note.defaultValue;
      syncGiftNote(form.elements.gift_option);
      if (form.isConnected && !form.hidden) form.elements.gift_option.focus();
    } catch (error) {
      statusFor(form).textContent = error.message;
      form.querySelector('.line-item__gift-editor-fallback').hidden = false;
    }
  });

  document.addEventListener('submit', async event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-gift-editor]')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (updating || !form.dataset.loaded || !form.reportValidity()) return;

    updating = true;
    const submit = form.querySelector('[type="submit"]');
    const choice = selectedChoice(form.elements.gift_option);
    submit.disabled = true;
    form.setAttribute('aria-busy', 'true');
    statusFor(form).textContent = 'Updating gift options…';

    try {
      const cartResponse = await fetch(`${root()}cart.js`, { cache: 'no-store' });
      if (!cartResponse.ok) throw new Error('Unable to read your bag. Please try again.');
      const before = await cartResponse.json();
      const item = before.items.find(line => line.key === form.dataset.giftLineKey);
      if (!item) throw new Error('This item has changed. Close and reopen the bag before editing it.');

      const group = item.properties?._gift_group || newGroup();
      const oldGift = before.items.find(line => line.properties?._gift_group === group && line.properties?._gift_addon === 'true');
      const properties = { ...(item.properties || {}), _gift_group: group };
      const note = form.elements.gift_note;
      if (note && !note.disabled && note.value.trim()) properties['Gift note'] = note.value.trim();
      else delete properties['Gift note'];

      if (choice.id) {
        properties[propertyName] = choice.label;
        properties._gift_variant = choice.id;
        if (!oldGift || String(oldGift.variant_id) !== String(choice.id)) {
          await request('cart/add.js', {
            items: [{ id: String(choice.id), quantity: 1, properties: { _gift_group: group, _gift_addon: 'true', _gift_for: item.product_title } }],
          });
        }
      } else {
        delete properties[propertyName];
        delete properties._gift_variant;
      }

      await request('cart/change.js', { id: item.key, quantity: item.quantity, properties });
      if (oldGift && (!choice.id || String(oldGift.variant_id) !== String(choice.id))) {
        await request('cart/change.js', { id: oldGift.key, quantity: 0 });
      }

      let announcement = document.getElementById('gift-options-announcement');
      if (!announcement) {
        announcement = document.createElement('p');
        announcement.id = 'gift-options-announcement';
        announcement.className = 'sr-only';
        announcement.setAttribute('role', 'status');
        document.body.append(announcement);
      }
      announcement.textContent = choice.id ? `Gift options updated: ${choice.label}.` : 'Gift options removed.';
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    } catch (error) {
      statusFor(form).textContent = error.message || 'Unable to update gift options. Please try again.';
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    } finally {
      updating = false;
      form.removeAttribute('aria-busy');
      submit.disabled = false;
    }
  }, true);
})();

// Mouse dragging for overflowing product lists only. Touch scrolling stays
// native; delegated listeners also cover editor reloads and fetched sections.
(() => {
  const threshold = 8;
  let gesture = null;
  let suppressedClick = null;

  function finish(event) {
    if (!gesture || (event?.pointerId != null && event.pointerId !== gesture.pointerId)) return;
    const current = gesture;
    gesture = null;
    if (current.dragging) {
      suppressedClick = { carousel: current.carousel, until: performance.now() + 500 };
      current.carousel.classList.remove('is-pointer-dragging');
      if (current.carousel.hasPointerCapture(current.pointerId)) {
        current.carousel.releasePointerCapture(current.pointerId);
      }
    }
  }

  document.addEventListener('pointerdown', (event) => {
    suppressedClick = null;
    if (event.pointerType !== 'mouse' || event.button !== 0 || !event.isPrimary) return;
    const target = event.target instanceof Element ? event.target : null;
    const carousel = target?.closest('product-list.scroll-area');
    if (!carousel || carousel.hasAttribute('allow-drag') || carousel.scrollWidth <= carousel.clientWidth + 1) return;
    if (target.closest('button, input, select, textarea, label, [role="button"], [contenteditable="true"]')) return;
    gesture = {
      carousel, pointerId: event.pointerId, x: event.clientX, y: event.clientY,
      scrollLeft: carousel.scrollLeft, dragging: false,
    };
  });

  document.addEventListener('pointermove', (event) => {
    if (!gesture || gesture.pointerId !== event.pointerId) return;
    if (!(event.buttons & 1) || !gesture.carousel.isConnected) { finish(event); return; }
    const dx = event.clientX - gesture.x;
    const dy = event.clientY - gesture.y;
    if (!gesture.dragging) {
      if (Math.abs(dx) < threshold && Math.abs(dy) < threshold) return;
      if (Math.abs(dy) > Math.abs(dx)) { gesture = null; return; }
      gesture.dragging = true;
      gesture.carousel.classList.add('is-pointer-dragging');
      gesture.carousel.setPointerCapture(event.pointerId);
    }
    event.preventDefault();
    gesture.carousel.scrollLeft = gesture.scrollLeft - dx;
  }, { passive: false });

  document.addEventListener('pointerup', finish);
  document.addEventListener('pointercancel', finish);
  document.addEventListener('lostpointercapture', finish);
  window.addEventListener('blur', () => finish());

  // Stop the browser's image/link ghost drag from stealing our gesture.
  document.addEventListener('dragstart', (event) => {
    if (gesture?.carousel.contains(event.target)) event.preventDefault();
  });

  // Capture before links, quick-buy handlers, or delegated theme click actions.
  // Keyboard clicks and the next deliberate pointer click remain unaffected.
  document.addEventListener('click', (event) => {
    if (!suppressedClick || event.detail === 0) return;
    if (performance.now() <= suppressedClick.until && suppressedClick.carousel.contains(event.target)) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
    suppressedClick = null;
  }, true);
})();

document.addEventListener(
  "submit",
  async (event) => {
    const submitter = event.submitter;

    if (!(submitter instanceof HTMLButtonElement) || !submitter.hasAttribute("data-buy-now-to-checkout")) {
      return;
    }

    const form = event.target;

    if (!(form instanceof HTMLFormElement) || submitter.getAttribute("aria-busy") === "true") {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    const submitButtons = Array.from(form.elements).filter(
      (element) => element instanceof HTMLButtonElement && element.type === "submit"
    );

    submitButtons.forEach((button) => button.setAttribute("aria-busy", "true"));

    const root = window.Shopify?.routes?.root || "/";

    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: "POST",
        headers: {
          "X-Requested-With": "XMLHttpRequest"
        },
        body: new FormData(form)
      });

      const responseJson = await response.json();

      if (!response.ok) {
        const message = responseJson.description || responseJson.message || "This item could not be added to your cart.";

        form.dispatchEvent(
          new CustomEvent("cart:error", {
            bubbles: true,
            detail: { error: message }
          })
        );

        document.dispatchEvent(
          new CustomEvent("toast:show", {
            detail: { message, tone: "error" }
          })
        );

        return;
      }

      window.location.assign(`${root}checkout`);
    } catch (error) {
      document.dispatchEvent(
        new CustomEvent("toast:show", {
          detail: {
            message: "This item could not be added to your cart. Please try again.",
            tone: "error"
          }
        })
      );
    } finally {
      submitButtons.forEach((button) => button.removeAttribute("aria-busy"));
    }
  },
  true
);
